import { Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { useAuth } from './core/hooks/useAuth';
import { AuthProvider } from './core/contexts/AuthContext';
import Layout from './core/components/Layout';
import Login from './pages/Login';
import PublicWelcome from './pages/PublicWelcome';
import { useActiveModules } from './core/modules/registry';
import type { FrontendModule } from './core/modules/types';

function Spinner() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({
  children,
  menuKey,
  modules,
}: {
  children: ReactNode;
  menuKey?: string;
  modules: FrontendModule[];
}) {
  const { isAuthenticated, isLoading, hasMenuAccess } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (window.location.pathname === '/') {
      // A module may send visitors somewhere public (e.g. the last opened program).
      for (const mod of modules) {
        const target = mod.publicLanding?.();
        if (target) return <Navigate to={target} replace />;
      }
      return <PublicWelcome />;
    }
    return <Navigate to="/login" replace />;
  }

  // Check menu-based access if menuKey is provided
  if (menuKey && !hasMenuAccess(menuKey)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Wrap the signed-in layout in the context providers of all active modules. */
function ModuleProviders({ modules, children }: { modules: FrontendModule[]; children: ReactNode }) {
  return <>{modules
    .flatMap(m => m.providers ?? [])
    .reduceRight<ReactNode>((inner, Provider) => <Provider>{inner}</Provider>, children)}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  const modules = useActiveModules();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route path="/auth/callback" element={<Login />} />
      {modules.flatMap(m => m.publicRoutes ?? []).map(route => (
        <Route
          key={route.path}
          path={route.path}
          element={<Suspense fallback={<Spinner />}><route.element /></Suspense>}
        />
      ))}

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute modules={modules}>
            <ModuleProviders modules={modules}>
              <Layout />
            </ModuleProviders>
          </ProtectedRoute>
        }
      >
        {modules.flatMap(m => m.routes ?? []).map(route => {
          const page = (
            <ProtectedRoute menuKey={route.menuKey} modules={modules}>
              <Suspense fallback={<Spinner />}><route.element /></Suspense>
            </ProtectedRoute>
          );
          return route.path === ''
            ? <Route key="index" index element={page} />
            : <Route key={route.path} path={route.path} element={page} />;
        })}

        {/* Legacy redirect */}
        <Route path="settings" element={<Navigate to="/admin/settings" replace />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
